/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { UiComponent } from 'components';
import { EnrollQuery, EventAdditionalQuery, EventFeedbackQuery } from 'queries';
import React, { useMemo } from 'react';
import { IQuestion } from 'types';
import { parseResponse, parseResponseValues } from 'utils/question';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const AGE_GROUPS = [
  { label: '만 19세 이하', max: 19 },
  { label: '만 20세~만 24세', max: 24 },
  { label: '만 25세~만 29세', max: 29 },
  { label: '만 30세~만 34세', max: 34 },
  { label: '만 35세~만 39세', max: 39 },
  { label: '만 40세 이상', max: Infinity },
];

const calcManAge = (birthDate: string, referenceDate: Date): number => {
  const birth = new Date(birthDate);
  let age = referenceDate.getFullYear() - birth.getFullYear();
  const notYet =
    referenceDate.getMonth() < birth.getMonth() ||
    (referenceDate.getMonth() === birth.getMonth() &&
      referenceDate.getDate() < birth.getDate());
  if (notYet) age--;
  return age;
};

const getAgeGroupLabel = (age: number): string =>
  AGE_GROUPS.find((g) => age <= g.max)?.label ?? '만 40세 이상';

function FeedbackModal() {
  const { id } = useParams<{ id: string }>();

  const { data } = EnrollQuery.useGetAllByEventId(Number(id || ''), true);
  const { data: feedback } = EventFeedbackQuery.useGet(id || '');
  const { data: additional } = EventAdditionalQuery.useGet(id || '');

  // additional.questions 중 SINGLE_CHOICE 타입이고 title에 '처음이신가요?' 포함한 가장 낮은 id의 질문
  const firstTimeQuestion = useMemo<IQuestion | null>(() => {
    if (!additional?.questions) return null;
    return (
      (additional.questions as IQuestion[])
        .filter(
          (q) =>
            q.type === 'SINGLE_CHOICE' && q.title.includes('처음이신가요?'),
        )
        .sort((a, b) => a.id - b.id)[0] ?? null
    );
  }, [additional?.questions]);

  const stats = useMemo(() => {
    if (!firstTimeQuestion || !data || !additional?.event?.updatedAt)
      return null;

    const referenceDate = new Date(additional.event.updatedAt);
    const respondents = data.filter((enroll: any) => enroll?.feedback?.length);

    const result = {
      total: respondents.length,
      returning: { total: 0, male: 0, female: 0 },
      new: {
        total: 0,
        male: 0,
        female: 0,
        ageGroups: {} as Record<string, number>,
      },
    };

    respondents.forEach((enroll: any) => {
      const firstTimeResponse = enroll.additional?.find(
        (r: { id: string; response: string }) =>
          r.id === String(firstTimeQuestion.id),
      );

      const isNew =
        firstTimeResponse?.response === firstTimeQuestion?.options?.[0].value;
      const gender: 'MALE' | 'FEMALE' = enroll.user.gender;

      if (isNew) {
        result.new.total++;
        if (gender === 'MALE') result.new.male++;
        if (gender === 'FEMALE') result.new.female++;
        if (enroll.user?.birthDate) {
          const group = getAgeGroupLabel(
            calcManAge(enroll.user.birthDate, referenceDate),
          );
          result.new.ageGroups[group] = (result.new.ageGroups[group] ?? 0) + 1;
        }
      } else {
        result.returning.total++;
        if (gender === 'MALE') result.returning.male++;
        if (gender === 'FEMALE') result.returning.female++;
      }
    });

    return result;
  }, [data, firstTimeQuestion]);

  const feedbackStats = useMemo(() => {
    if (!firstTimeQuestion || !data || !feedback?.questions) return null;

    const questions = feedback.questions as IQuestion[];
    const respondents = data.filter((enroll: any) => enroll?.feedback?.length);

    type LinearStat = {
      type: 'LINEAR_SCALE';
      id: number;
      title: string;
      minLabel: string;
      maxLabel: string;
      new: { sum: number; count: number };
      returning: { sum: number; count: number };
    };
    type TextStat = {
      type: 'TEXT';
      id: number;
      title: string;
      new: string[];
      returning: string[];
    };
    type ChoiceStat = {
      type: 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE';
      id: number;
      title: string;
      new: { options: Record<string, number>; total: number };
      returning: { options: Record<string, number>; total: number };
    };

    const linearMap = new Map<number, LinearStat>();
    const textMap = new Map<number, TextStat>();
    const choiceMap = new Map<number, ChoiceStat>();

    questions.forEach((q) => {
      if (q.type === 'LINEAR_SCALE') {
        const opts = q.options as any[];
        linearMap.set(q.id, {
          type: 'LINEAR_SCALE',
          id: q.id,
          title: q.title,
          minLabel: opts[0]?.value ?? '',
          maxLabel: opts[1]?.value ?? '',
          new: { sum: 0, count: 0 },
          returning: { sum: 0, count: 0 },
        });
      } else if (q.type === 'TEXT') {
        textMap.set(q.id, {
          type: 'TEXT',
          id: q.id,
          title: q.title,
          new: [],
          returning: [],
        });
      } else {
        choiceMap.set(q.id, {
          type: q.type as 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE',
          id: q.id,
          title: q.title,
          new: { options: {}, total: 0 },
          returning: { options: {}, total: 0 },
        });
      }
    });

    respondents.forEach((enroll: any) => {
      const firstTimeResponse = enroll.additional?.find(
        (r: { id: string; response: string }) =>
          r.id === String(firstTimeQuestion.id),
      );
      const isNew =
        firstTimeResponse?.response === firstTimeQuestion?.options?.[0].value;

      (enroll.feedback as { id: string; response: string }[]).forEach(
        ({ id, response }) => {
          const qId = Number(id);
          if (linearMap.has(qId)) {
            const val = Number(response);
            if (!isNaN(val)) {
              const stat = linearMap.get(qId)!;
              if (isNew) {
                stat.new.sum += val;
                stat.new.count++;
              } else {
                stat.returning.sum += val;
                stat.returning.count++;
              }
            }
          } else if (textMap.has(qId)) {
            const parsed = parseResponse(response).trim();
            if (parsed) {
              const stat = textMap.get(qId)!;
              if (isNew) stat.new.push(parsed);
              else stat.returning.push(parsed);
            }
          } else if (choiceMap.has(qId)) {
            const stat = choiceMap.get(qId)!;
            const group = isNew ? stat.new : stat.returning;
            group.total++;
            parseResponseValues(response).forEach((val) => {
              group.options[val] = (group.options[val] ?? 0) + 1;
            });
          }
        },
      );
    });

    return {
      linear: Array.from(linearMap.values()),
      text: Array.from(textMap.values()),
      choice: Array.from(choiceMap.values()),
    };
  }, [data, feedback, firstTimeQuestion]);

  if (!data || !feedback || !additional || !stats) return null;

  const totalCount = data.length;
  const respondentRatio =
    totalCount > 0 ? Math.round((stats.total / totalCount) * 100) : 0;
  const avg = (sum: number, count: number) =>
    count > 0 ? (sum / count).toFixed(2) : '-';

  // "3. 제목" → "제목", "[앱 사용성 설문조사] 1. 제목" → "[앱 사용성 설문조사] 제목"
  const stripPrefix = (title: string) =>
    title.replace(/^(\[.*?\]\s*)?\d+\.\s*/, '$1').trim();

  const orderedAgeGroups = AGE_GROUPS.map((g) => ({
    label: g.label,
    count: stats.new.ageGroups[g.label] ?? 0,
  })).filter((g) => g.count > 0);

  const basicCopyLines = [
    `**기본 사항**`,
    `- 응답자 : ${stats.total}명 (전체 참여자 약 ${totalCount}명, ${respondentRatio}%)`,
    `    - 기존 : ${stats.returning.total}명`,
    `        - 남성 : ${stats.returning.male}명`,
    `        - 여성 : ${stats.returning.female}명`,
    `    - 신규 : ${stats.new.total}명`,
    `        - 남성 : ${stats.new.male}명`,
    `        - 여성 : ${stats.new.female}명`,
    `        - 신규 연령대 비율`,
    ...orderedAgeGroups.map((g) => `            - ${g.label} : ${g.count}명`),
  ];

  const pct = (count: number, total: number) =>
    total > 0 ? `${Math.round((count / total) * 100)}%` : '0%';

  const satisfactionCopyLines = feedbackStats
    ? [
        ``,
        `**만족도**`,
        ...feedbackStats.linear.flatMap((q) => [
          `- ${stripPrefix(q.title)} (${q.minLabel} - ${q.maxLabel})`,
          `    - 기존 : ${avg(q.returning.sum, q.returning.count)}점`,
          `    - 신규 : ${avg(q.new.sum, q.new.count)}점`,
        ]),
      ]
    : [];

  const choiceCopyLines = feedbackStats
    ? [
        ``,
        `**피드백**`,
        ...feedbackStats.choice.flatMap((q) => [
          `- ${stripPrefix(q.title)}`,
          `    - 기존 (${q.returning.total}명)`,
          ...Object.entries(q.returning.options).map(
            ([val, cnt]) =>
              `        - ${val} : ${cnt}명 (${pct(cnt, q.returning.total)})`,
          ),
          `    - 신규 (${q.new.total}명)`,
          ...Object.entries(q.new.options).map(
            ([val, cnt]) =>
              `        - ${val} : ${cnt}명 (${pct(cnt, q.new.total)})`,
          ),
        ]),
      ]
    : [];

  const etcCopyLines = feedbackStats
    ? [
        ``,
        `**기타**`,
        ...feedbackStats.text.flatMap((q) => [
          `- ${stripPrefix(q.title)}`,
          `    - 기존`,
          ...q.returning.map((r) => `        - ${r}`),
          `    - 신규`,
          ...q.new.map((r) => `        - ${r}`),
        ]),
      ]
    : [];

  const copyText = [
    ...basicCopyLines,
    ...satisfactionCopyLines,
    ...choiceCopyLines,
    ...etcCopyLines,
  ].join('\n');

  const subList = { paddingLeft: '16px' };
  const listStyle = { fontSize: '14px', lineHeight: '2.2' as const };

  return (
    <div css={{ padding: '16px 0' }}>
      <div
        css={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <UiComponent.Button
          color="primary"
          variant="border"
          css={{ height: '30px', fontSize: '12px' }}
          onClick={() => {
            try {
              navigator.clipboard.writeText(copyText);
              toast.success('복사 완료');
            } catch {
              toast.error('복사 실패');
            }
          }}
        >
          전체 복사
        </UiComponent.Button>
      </div>

      {/* 기본 사항 */}
      <div css={{ marginBottom: '24px' }}>
        <h3 css={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '8px' }}>
          기본 사항
        </h3>
        <ul css={listStyle}>
          <li>
            응답자 : <b>{stats.total}명</b>{' '}
            <span css={{ color: '#8d8d8d' }}>
              (전체 참여자 약 {totalCount}명, {respondentRatio}%)
            </span>
          </li>
          <li css={subList}>
            기존 : <b>{stats.returning.total}명</b>
            <ul css={subList}>
              <li>남성 : {stats.returning.male}명</li>
              <li>여성 : {stats.returning.female}명</li>
            </ul>
          </li>
          <li css={subList}>
            신규 : <b>{stats.new.total}명</b>
            <ul css={subList}>
              <li>남성 : {stats.new.male}명</li>
              <li>여성 : {stats.new.female}명</li>
              <li>
                신규 연령대 비율
                <ul css={subList}>
                  {orderedAgeGroups.map((g) => (
                    <li key={g.label}>
                      {g.label} : {g.count}명
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      {/* 만족도 */}
      {feedbackStats && (
        <div css={{ marginBottom: '24px' }}>
          <h3
            css={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '8px' }}
          >
            만족도
          </h3>
          <ul css={listStyle}>
            {feedbackStats.linear.map((q) => (
              <li key={q.id}>
                <b>{stripPrefix(q.title)}</b>{' '}
                <span css={{ color: '#8d8d8d', fontSize: '12px' }}>
                  ({q.minLabel} - {q.maxLabel})
                </span>
                <ul css={subList}>
                  <li>기존 : {avg(q.returning.sum, q.returning.count)}점</li>
                  <li>신규 : {avg(q.new.sum, q.new.count)}점</li>
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 피드백 (선택형) */}
      {feedbackStats && feedbackStats.choice.length > 0 && (
        <div>
          <h3
            css={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '8px' }}
          >
            피드백
          </h3>
          <ul css={listStyle}>
            {feedbackStats.choice.map((q) => (
              <li key={q.id}>
                <b>{stripPrefix(q.title)}</b>
                <ul css={subList}>
                  <li>
                    기존 ({q.returning.total}명)
                    <ul css={subList}>
                      {Object.entries(q.returning.options).map(([val, cnt]) => (
                        <li key={val}>
                          {val} : {cnt}명 ({pct(cnt, q.returning.total)})
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li>
                    신규 ({q.new.total}명)
                    <ul css={subList}>
                      {Object.entries(q.new.options).map(([val, cnt]) => (
                        <li key={val}>
                          {val} : {cnt}명 ({pct(cnt, q.new.total)})
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 기타 (주관식) */}
      {feedbackStats && feedbackStats.text.length > 0 && (
        <div css={{ marginTop: '24px' }}>
          <h3
            css={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '8px' }}
          >
            기타
          </h3>
          <ul css={listStyle}>
            {feedbackStats.text.map((q) => (
              <li key={q.id}>
                <b>{stripPrefix(q.title)}</b>
                <ul css={subList}>
                  <li>
                    기존
                    <ul css={subList}>
                      {q.returning.length > 0 ? (
                        q.returning.map((r, i) => <li key={i}>{r}</li>)
                      ) : (
                        <li css={{ color: '#8d8d8d' }}>없음</li>
                      )}
                    </ul>
                  </li>
                  <li>
                    신규
                    <ul css={subList}>
                      {q.new.length > 0 ? (
                        q.new.map((r, i) => <li key={i}>{r}</li>)
                      ) : (
                        <li css={{ color: '#8d8d8d' }}>없음</li>
                      )}
                    </ul>
                  </li>
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default FeedbackModal;
